'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState, memo } from 'react';
import { ArrowUpRight } from 'lucide-react';

import type { PropertyCardProps as ThemePropertyCardProps } from '@routeroyal/theme-core';
import type { FeaturedPlacementSummary, FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import { FeaturedBadge } from '@/components/listings/FeaturedBadge';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { WishlistButton } from '@/components/customer/WishlistButton';
// Structured location from the Route Royal API
interface PropertyLocationData {
  city?: string | null;
  suburb?: string | null;
  district?: string | null;
  neighbourhood?: string | null;
  region?: string | null;
  country?: string | null;
  countryCode?: string | null;
  countryName?: string | null;
  localityName?: string | null;
  administrativeAreaName?: string | null;
}

// Local property type matching Route Royal API
interface PropertyApiType {
  id: string;
  name: string;
  description?: string | null;
  status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'DELETED';
  listingStatus?: string | null;
  listingType?: string | null;
  primaryImageUrl?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  totalFloorArea?: number | null;
  totalFloorAreaUnit?: string | null;
  currency?: string | null;
  currentAskingCurrency?: string | null;
  currentAskingPrice?: number | null;
  location?: PropertyLocationData | null;
  attributeValues?: {
    askingPrice?: {
      valueNumber?: number | null;
      valueString?: string;
    };
  } | null;
}

/**
 * Map theme-core property format to API format
 */
function mapThemePropertyToApiProperty(themeProperty: ThemePropertyCardProps['property']): PropertyApiType {
  const priceMatch = themeProperty.price?.match(/[\d,.]+/);
  const priceValue = priceMatch ? parseFloat(priceMatch[0].replace(/,/g, '')) : 0;

  return {
    id: themeProperty.id,
    name: themeProperty.name,
    description: themeProperty.address,
    status: themeProperty.status === 'sold' ? 'ARCHIVED' : themeProperty.status === 'reserved' ? 'INACTIVE' : 'ACTIVE',
    listingStatus: themeProperty.status === 'sold' ? 'SOLD' : null,
    primaryImageUrl: themeProperty.image,
    bedrooms: themeProperty.bedrooms ?? null,
    bathrooms: themeProperty.bathrooms ?? null,
    totalFloorArea: themeProperty.area ? parseFloat(themeProperty.area) : null,
    totalFloorAreaUnit: 'SQM',
    currency: DEFAULT_CURRENCY,
    attributeValues: {
      askingPrice: {
        valueNumber: priceValue,
        valueString: themeProperty.price,
      },
    },
  };
}

export interface StoneInvestmentPropertyCardProps {
  property: PropertyApiType | ThemePropertyCardProps['property'];
  variant?: 'default' | 'compact' | 'featured' | 'portfolio' | 'landscape' | 'portrait';
  locale?: string;
  showPrice?: boolean;
  showStatus?: boolean;
  /** Featured placements for this property */
  featuredPlacements?: FeaturedPlacementSummary[];
  /** Whether to show the featured badge */
  showFeaturedBadge?: boolean;
  /** Whether to show original price alongside converted price */
  showOriginalPrice?: boolean;
  /** Whether to show the wishlist (heart) button */
  showWishlistButton?: boolean;
  index?: number;
  onClick?: () => void;
  className?: string;
  translations?: Record<string, string>;
}

/**
 * HorizonOverlay Component
 * 
 * Concept One Design (Exact Match):
 * - Dark gradient overlay that fades in from bottom on hover
 * - Flag color strip at the very bottom
 * - France: blue/white/red (vertical tricolor)
 * - Mauritius: red/blue/yellow/green (4 stripes)
 */
export const HorizonOverlay: React.FC<{ isHovered: boolean; isFrance: boolean }> = memo(({ isHovered, isFrance }) => (
  <>
    {/* 1. Single Color Gradient Overlay (Dark Stone/Black) - Fades in from bottom */}
    <div className={`absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent transition-opacity duration-1000 ease-out pointer-events-none ${isHovered ? 'opacity-100' : 'opacity-0'}`} />

    {/* 2. The Horizon Strip - Flag Colors at the very bottom */}
    <div className="absolute bottom-0 left-0 w-full h-1.5 flex z-30 pointer-events-none">
      {isFrance ? (
        <>
          <div className={`h-full flex-1 bg-[#002654] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '0ms' }} />
          <div className={`h-full flex-1 bg-white transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }} />
          <div className={`h-full flex-1 bg-[#ED2939] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '200ms' }} />
        </>
      ) : (
        <>
          <div className={`h-full flex-1 bg-[#EA2839] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '0ms' }} />
          <div className={`h-full flex-1 bg-[#1A206D] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '75ms' }} />
          <div className={`h-full flex-1 bg-[#FFD500] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '150ms' }} />
          <div className={`h-full flex-1 bg-[#00A551] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '225ms' }} />
        </>
      )}
    </div>
  </>
));

HorizonOverlay.displayName = 'HorizonOverlay';

/**
 * Stone Investment Property Card Component
 * 
 * Concept One Design (Exact Match):
 * - HorizonOverlay with animated flag colors on hover
 * - Floating circular "View" button with spinning border
 * - Image zoom effect (2000ms duration)
 * - font-serif for title, font-sans for metadata
 * - Italic title on hover
 * - Now uses FormatPrice for currency-aware price display
 */
export const StoneInvestmentPropertyCard = memo(function StoneInvestmentPropertyCard({ 
  property: rawProperty, 
  variant = 'landscape', 
  locale = 'en',
  showPrice = true,
  showStatus = true,
  featuredPlacements = [],
  showFeaturedBadge = true,
  showOriginalPrice = false,
  showWishlistButton = true,
  index: _index = 0, // Reserved for stagger animations
  onClick,
  className = '',
  translations = {},
}: StoneInvestmentPropertyCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const tt = (key: string, fallback: string): string => translations[key] || fallback;

  if (!rawProperty) {
    return null;
  }
  
  // Get primary featured badge
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  // Normalize property to API format
  const property = 'status' in rawProperty && typeof rawProperty.status === 'string' && ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED', 'DELETED'].includes(rawProperty.status)
    ? rawProperty as PropertyApiType
    : mapThemePropertyToApiProperty(rawProperty as ThemePropertyCardProps['property']);

  const askingPrice = property.attributeValues?.askingPrice?.valueNumber ?? property.currentAskingPrice;
  const currency = property.currentAskingCurrency ?? property.currency ?? DEFAULT_CURRENCY;
  const hasPrice = askingPrice && askingPrice > 0;

  const area = property.totalFloorArea 
    ? `${property.totalFloorArea}`
    : null;

  const isSold = property.status === 'ARCHIVED'
    || property.status === 'INACTIVE'
    || property.listingStatus === 'SOLD';

  const locationCountry = property.location?.country || property.location?.countryName || '';
  const locationCountryCode = (property.location?.countryCode || '').toLowerCase();
  const isFrance = locationCountryCode === 'fr' || locationCountry.toLowerCase() === 'france';
  const countryLabel = locationCountry
    || (locationCountryCode === 'fr' ? 'France' : locationCountryCode === 'mu' ? 'Mauritius' : '');

  const aspectRatio = variant === 'portrait' ? 'aspect-[3/4]' : 'aspect-video';

  const CardContent = (
    <div 
      className={`group relative w-full cursor-pointer flex flex-col gap-6 ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className={`relative w-full ${aspectRatio} overflow-hidden bg-stone-200`}>
        {/* Base Image with Zoom Effect */}
            {property.primaryImageUrl ? (
              <Image
                src={property.primaryImageUrl}
                alt={property.name}
                fill
            loading="lazy"
            className={`object-cover transition-transform duration-[2000ms] ease-out will-change-transform ${
              isHovered ? 'scale-105' : 'scale-100'
            }`}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
            ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-stone-300 to-stone-200" />
        )}

        <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />

        {/* Center Floating "View" Button */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex items-center justify-center pointer-events-none">
          <div 
            className={`relative flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
              isHovered ? 'w-24 h-24 opacity-100' : 'w-12 h-12 opacity-0 translate-y-4'
            }`}
          >
            <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
            <span className="text-white font-serif italic text-xl tracking-widest drop-shadow-md">
              {tt('view', 'View')}
                  </span>
                </div>
              </div>
        
        {/* Region Text Label - Bottom Left */}
        <div className="absolute bottom-6 left-6 z-20 overflow-hidden">
          <p 
            className={`text-[10px] uppercase tracking-[0.3em] text-stone-200 font-sans font-medium transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              isHovered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
            style={{ transitionDelay: '100ms' }}
            >
              {property.description || 'Location'}
            </p>
          </div>

        {/* Wishlist Button - positioned top-left when no status badge */}
        {showWishlistButton && !isSold && (
          <div className="absolute top-6 left-6 z-20">
            <WishlistButton
              entityType="PROPERTY"
              entityId={property.id}
              variant="solid"
              size="md"
              className="bg-white/90 hover:bg-white shadow-sm"
            />
          </div>
        )}

        {/* Status Badge */}
        {showStatus && isSold && (
          <div className="absolute top-6 left-6 z-20">
            <span className="text-[10px] tracking-[0.25em] uppercase px-4 py-2 bg-white text-stone-900 font-sans">
              {tt('sold', 'Sold')}
            </span>
          </div>
        )}
        
        {/* Featured Badge - positioned top-right */}
        {primaryFeatured?.badgeVariant && !isSold && (
          <div className="absolute top-6 right-6 z-20">
            <FeaturedBadge
              variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
              label={primaryFeatured.label}
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Content Section - Minimal & Typographic */}
      <div className="flex flex-col gap-4 relative">
        <div className="flex justify-between items-baseline border-t border-stone-200 pt-6">
          <h3 className={`font-serif text-3xl md:text-4xl text-stone-900 leading-none transition-all duration-500 ${isHovered ? 'italic' : ''}`}>
            {property.name}
          </h3>
          {showPrice && hasPrice && (
            <span className="font-sans text-sm font-medium text-stone-900 shrink-0 ml-4">
              <FormatPrice
                amount={askingPrice}
                currency={currency}
                size="sm"
                showOriginal={showOriginalPrice}
                className="!text-stone-900"
              />
            </span>
          )}
        </div>

        <div className="flex items-center gap-4 text-stone-700 font-sans text-sm tracking-widest uppercase">
          {property.bedrooms && property.bedrooms > 0 && <span>{property.bedrooms} {tt('beds', 'Beds')}</span>}
          {property.bedrooms && property.bedrooms > 0 && area && <span className="w-px h-3 bg-stone-500" />}
          {area && <span>{area} m²</span>}
          {countryLabel && (
            <>
              <span className="w-px h-3 bg-stone-500" />
              <span className={`transition-colors duration-500 ${isFrance ? 'group-hover:text-blue-900' : 'group-hover:text-teal-800'}`}>
                {countryLabel}
              </span>
            </>
          )}
        </div>
        
        {/* Arrow indicator on hover */}
        <div className={`absolute right-0 bottom-0 transition-all duration-500 ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
          <ArrowUpRight size={24} className="text-stone-900" strokeWidth={1} />
        </div>
      </div>
    </div>
  );

  // Wrap in Link or div based on onClick
  if (onClick) {
    return (
      <div onClick={onClick}>
        {CardContent}
      </div>
    );
  }

  return (
    <Link href={`/${locale}/properties/${property.id}`}>
      {CardContent}
    </Link>
  );
});

export default StoneInvestmentPropertyCard;
